import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import Modal from '../components/Modal.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { mediaApi } from '../api/media.js';
import { apiError } from '../api/client.js';

const ALL_FOLDERS = '__all__';
const ROOT_FOLDER = '__root__';
const PUBVORA_MEDIA_MIME = 'application/x-pubvora-media';
const PER_PAGE = 60;
const HOVER_EXPAND_MS = 600; // dwell time before a collapsed folder auto-expands

// Workspace-wide image picker + manager.
//
// Folders are slash-separated path strings ("marketing/2026/launch"); the
// sidebar builds a tree from the workspace's distinct paths.
//
// Tiles have two independent selection states:
// - "picked" (single, drives the modal's onSelect output)
// - "bulk-selected" (a Set, drives bulk delete / move / drag-to-folder)
//
// Drag interactions:
// - Drag a tile onto a folder row → moves that tile (or all bulk-selected
//   tiles if the dragged tile is part of the bulk set) into the folder.
// - Drag external files (from the OS) onto a folder row → uploads them
//   into that folder.
// - The file picker (↑ Upload) accepts multiple files in one go.
export default function MediaLibraryModal({ open, onClose, onSelect, currentUrl }) {
  const { workspace } = useAuth();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(ALL_FOLDERS);
  const [loading, setLoading] = useState(false);
  const [pickedUrl, setPickedUrl] = useState(currentUrl || '');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total, label }
  const [filter, setFilter] = useState('');
  // Debounced version of filter that actually hits the server. Reset to page
  // 1 whenever it changes.
  const [query, setQuery] = useState('');
  const [bulkIds, setBulkIds] = useState(() => new Set());
  const [lastBulkId, setLastBulkId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [dragOverFolder, setDragOverFolder] = useState(null); // path | ROOT_FOLDER | null
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Failed uploads we offer to retry. Each: { file: File, error: string }.
  const [failedUploads, setFailedUploads] = useState([]);
  // Latest folder/filter token — used to drop late page responses if the
  // user navigates while a fetch is in flight.
  const fetchTokenRef = useRef(0);

  function folderParam() {
    return folder === ALL_FOLDERS
      ? undefined
      : folder === ROOT_FOLDER
        ? ''
        : folder;
  }

  // Debounce the filter input → query (server-side search term).
  useEffect(() => {
    const handle = setTimeout(() => setQuery(filter.trim()), 300);
    return () => clearTimeout(handle);
  }, [filter]);

  // Initial / folder-change / query-change load. Resets to page 1.
  useEffect(() => {
    if (!open || !workspace) return;
    setLoading(true);
    setPickedUrl(currentUrl || '');
    setBulkIds(new Set());
    setLastBulkId(null);
    setItems([]);
    setPage(1);
    setHasMore(false);

    const token = ++fetchTokenRef.current;
    mediaApi
      .list(workspace.id, {
        folder: folderParam(),
        page: 1,
        perPage: PER_PAGE,
        q: query || undefined,
      })
      .then((data) => {
        if (token !== fetchTokenRef.current) return;
        setItems(data.media || []);
        setFolders(data.folders || []);
        setHasMore(!!data.pagination?.has_more);
      })
      .catch((err) => toast.error(apiError(err)))
      .finally(() => {
        if (token === fetchTokenRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspace, currentUrl, folder, query]);

  // Load the next page and append. No-op if already loading or out of pages.
  const loadMore = useCallback(async () => {
    if (!workspace || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const token = fetchTokenRef.current;
    try {
      const nextPage = page + 1;
      const data = await mediaApi.list(workspace.id, {
        folder: folderParam(),
        page: nextPage,
        perPage: PER_PAGE,
        q: query || undefined,
      });
      if (token !== fetchTokenRef.current) return;
      setItems((cur) => [...cur, ...(data.media || [])]);
      setPage(nextPage);
      setHasMore(!!data.pagination?.has_more);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      if (token === fetchTokenRef.current) setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, loadingMore, hasMore, page, folder, query]);

  // Server does the filtering via the `q` param, so the loaded list is
  // exactly what we render.
  const visible = items;

  // Virtualization: track scroll position + container size and compute the
  // visible row range. Tile width / height are fixed estimates; the column
  // count is derived from the container width. See useVirtualGrid below.
  const scrollRef = useRef(null);
  const grid = useVirtualGrid(scrollRef, visible.length, {
    rowHeight: 160,
    minColumnWidth: 130,
    gap: 12,
    overscan: 4,
  });

  // Replace the IntersectionObserver with a scroll-position check —
  // virtualization makes the DOM-based sentinel awkward (it sits inside
  // an absolutely-positioned slice), so we just look at scrollTop directly.
  function onScroll(e) {
    grid.handleScroll(e);
    const el = e.currentTarget;
    if (
      hasMore &&
      !loadingMore &&
      el.scrollTop + el.clientHeight > el.scrollHeight - 300
    ) {
      loadMore();
    }
  }

  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  function targetFolderForUpload() {
    if (folder === ALL_FOLDERS || folder === ROOT_FOLDER) return null;
    return folder;
  }

  // Multi-file upload (used by the picker and by file-drops on folders).
  // Pass `clearPrevFailures=true` to wipe the retry banner first; pass
  // `false` from the retry path so a partial second failure accumulates.
  async function uploadFiles(files, folderOverride, clearPrevFailures = true) {
    if (!files.length || !workspace) return;
    const target = folderOverride !== undefined ? folderOverride : targetFolderForUpload();
    if (clearPrevFailures) setFailedUploads([]);

    setUploading(true);
    setProgress({ done: 0, total: files.length, label: files[0]?.name });
    const tId = toast.loading(`Uploading 0/${files.length}…`);
    try {
      const results = await mediaApi.uploadMany(workspace.id, files, {
        folder: target,
        onProgress: (done, total, label) => {
          setProgress({ done, total, label });
          toast.loading(`Uploading ${done}/${total}…`, { id: tId });
        },
      });
      const ok = results.filter((r) => r && !r.error);
      setItems((cur) => [...ok, ...cur]);
      const newFolders = ok
        .map((m) => m.folder)
        .filter((f) => f && !folders.includes(f));
      if (newFolders.length) {
        setFolders((cur) => Array.from(new Set([...cur, ...newFolders])).sort());
      }

      // Pair each result with its source file by index so we can offer a
      // targeted retry. uploadMany preserves order in the results array.
      const failures = [];
      results.forEach((r, i) => {
        if (r?.error) {
          failures.push({ file: files[i], error: apiError(r.error), folder: target });
        }
      });
      setFailedUploads((cur) => (clearPrevFailures ? failures : [...cur, ...failures]));

      const failed = failures.length;
      if (failed > 0) toast.error(`${ok.length} uploaded, ${failed} failed`, { id: tId });
      else toast.success(`Uploaded ${ok.length}`, { id: tId });
      if (ok[0]) setPickedUrl(ok[0].url);
    } catch (err) {
      toast.error(apiError(err), { id: tId });
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }

  async function retryFailed() {
    if (!failedUploads.length) return;
    // Group by folder so each retry batch goes to the right place.
    const groups = new Map();
    for (const f of failedUploads) {
      const key = f.folder ?? '__none__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(f.file);
    }
    setFailedUploads([]);
    for (const [key, batch] of groups) {
      const target = key === '__none__' ? null : key;
      await uploadFiles(batch, target, /* clearPrevFailures */ false);
    }
  }

  async function pickFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await uploadFiles(files);
  }

  async function deleteOne(media) {
    const ok = window.confirm(`Delete "${media.filename}"? This can't be undone.`);
    if (!ok) return;
    try {
      await mediaApi.destroy(media.id);
      setItems((cur) => cur.filter((m) => m.id !== media.id));
      setBulkIds((cur) => {
        const next = new Set(cur);
        next.delete(media.id);
        return next;
      });
      if (pickedUrl === media.url) setPickedUrl('');
      toast.success('Deleted');
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  async function patchMedia(media, payload) {
    try {
      const updated = await mediaApi.update(media.id, payload);
      setItems((cur) => cur.map((m) => (m.id === media.id ? updated : m)));
      if (updated.folder && !folders.includes(updated.folder)) {
        setFolders((cur) => [...cur, updated.folder].sort());
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  function newFolder() {
    const name = window.prompt('New folder path (use "/" to nest, e.g. marketing/2026)');
    if (!name) return;
    const trimmed = sanitizePath(name);
    if (!trimmed) return;
    if (!folders.includes(trimmed)) setFolders((cur) => [...cur, trimmed].sort());
    setExpanded((cur) => {
      const next = new Set(cur);
      const parts = trimmed.split('/');
      for (let i = 1; i < parts.length; i++) next.add(parts.slice(0, i).join('/'));
      return next;
    });
    setFolder(trimmed);
  }

  function toggleBulk(media, withShift) {
    setBulkIds((cur) => {
      const next = new Set(cur);
      if (withShift && lastBulkId) {
        const ids = visible.map((m) => m.id);
        const a = ids.indexOf(lastBulkId);
        const b = ids.indexOf(media.id);
        if (a !== -1 && b !== -1) {
          const [from, to] = a < b ? [a, b] : [b, a];
          for (let i = from; i <= to; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(media.id)) next.delete(media.id);
      else next.add(media.id);
      return next;
    });
    setLastBulkId(media.id);
  }

  async function bulkDelete() {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    const ok = window.confirm(`Delete ${ids.length} item${ids.length === 1 ? '' : 's'}? This can't be undone.`);
    if (!ok) return;
    const tId = toast.loading(`Deleting ${ids.length}…`);
    try {
      const { count } = await mediaApi.bulkDestroy(workspace.id, ids);
      setItems((cur) => cur.filter((m) => !bulkIds.has(m.id)));
      setBulkIds(new Set());
      toast.success(`Deleted ${count}`, { id: tId });
    } catch (err) {
      toast.error(apiError(err), { id: tId });
    }
  }

  async function bulkMoveToFolder(folderValue) {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    let target = folderValue;
    if (target === undefined) {
      const input = window.prompt(
        `Move ${ids.length} item${ids.length === 1 ? '' : 's'} to folder (blank for none, "/" to nest):`,
        folder === ALL_FOLDERS || folder === ROOT_FOLDER ? '' : folder,
      );
      if (input === null) return;
      target = sanitizePath(input) || null;
    }
    await applyBulkMove(ids, target);
  }

  async function applyBulkMove(ids, folderValue) {
    if (!ids.length) return;
    const tId = toast.loading(`Moving ${ids.length}…`);
    try {
      const { media } = await mediaApi.bulkUpdate(workspace.id, ids, { folder: folderValue });
      const updatedById = new Map((media || []).map((u) => [u.id, u]));
      setItems((cur) => cur.map((m) => updatedById.get(m.id) || m));
      if (folderValue && !folders.includes(folderValue)) {
        setFolders((cur) => [...cur, folderValue].sort());
      }
      setBulkIds(new Set());
      toast.success(`Moved ${ids.length}`, { id: tId });
    } catch (err) {
      toast.error(apiError(err), { id: tId });
    }
  }

  function toggleExpanded(path) {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  // ── tile drag start ────────────────────────────────────────────────────
  // If the dragged tile is part of bulkIds, we move all of them; otherwise
  // we move just the dragged tile. Encoded as JSON in a custom mime so
  // folder targets can read it without colliding with file drops.
  function onTileDragStart(e, media) {
    const draggedIds = bulkIds.has(media.id) ? Array.from(bulkIds) : [media.id];
    e.dataTransfer.setData(PUBVORA_MEDIA_MIME, JSON.stringify({ ids: draggedIds }));
    e.dataTransfer.effectAllowed = 'move';

    // Build a stacked preview of up to 3 thumbnails when bulk-dragging.
    // Always include the anchor media first so the user sees what they
    // grabbed; pad with other selected items by id order.
    const stack = [media];
    if (draggedIds.length > 1) {
      const idsLeft = new Set(draggedIds.filter((id) => id !== media.id));
      for (const item of items) {
        if (stack.length >= 3) break;
        if (idsLeft.has(item.id)) stack.push(item);
      }
    }
    setCustomDragImage(e, stack, draggedIds.length);
  }

  // ── folder drop target ────────────────────────────────────────────────
  // Track the dwell timer per folder so we don't auto-expand if the user
  // is just passing through quickly.
  const expandTimerRef = useRef(null);
  const lastHoverFolderRef = useRef(null);

  function clearExpandTimer() {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
    lastHoverFolderRef.current = null;
  }

  function scheduleExpand(folderPath) {
    if (folderPath === ROOT_FOLDER || !folderPath) return;
    if (expanded.has(folderPath)) return;
    if (lastHoverFolderRef.current === folderPath) return;
    clearExpandTimer();
    lastHoverFolderRef.current = folderPath;
    expandTimerRef.current = setTimeout(() => {
      setExpanded((cur) => {
        const next = new Set(cur);
        next.add(folderPath);
        return next;
      });
      expandTimerRef.current = null;
    }, HOVER_EXPAND_MS);
  }

  function onFolderDragOver(e, folderPath) {
    const types = e.dataTransfer?.types || [];
    const hasMedia = Array.from(types).includes(PUBVORA_MEDIA_MIME);
    const hasFiles = Array.from(types).includes('Files');
    if (!hasMedia && !hasFiles) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = hasMedia ? 'move' : 'copy';
    setDragOverFolder(folderPath);
    scheduleExpand(folderPath);
  }

  function onFolderDragLeave() {
    setDragOverFolder(null);
    clearExpandTimer();
  }

  async function onFolderDrop(e, folderPath) {
    setDragOverFolder(null);
    clearExpandTimer();

    // Internal media drag (move).
    const mediaJson = e.dataTransfer.getData(PUBVORA_MEDIA_MIME);
    if (mediaJson) {
      e.preventDefault();
      try {
        const { ids } = JSON.parse(mediaJson);
        if (Array.isArray(ids) && ids.length) {
          const target = folderPath === ROOT_FOLDER ? null : folderPath;
          await applyBulkMove(ids, target);
        }
      } catch {
        // bad payload — ignore silently
      }
      return;
    }

    // External file drop (upload into this folder).
    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      (f.type || '').startsWith('image/'),
    );
    if (files.length) {
      e.preventDefault();
      const target = folderPath === ROOT_FOLDER ? null : folderPath;
      await uploadFiles(files, target);
    }
  }

  // Build an offscreen DOM node that the browser snapshots as the drag
  // image. We have to keep the node in the DOM long enough for the
  // snapshot to happen (a tick), then yank it.
  //
  // `stack` holds 1-3 media records — the first is the anchor (the tile the
  // user grabbed), additional entries fan out behind it.
  function setCustomDragImage(e, stack, count) {
    if (typeof document === 'undefined' || !e.dataTransfer?.setDragImage) return;

    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      'top:-1000px',
      'left:-1000px',
      'pointer-events:none',
      'display:flex',
      'align-items:center',
      'gap:10px',
      'padding:6px 12px 6px 8px',
      'border-radius:10px',
      'background:#fff',
      'border:2px solid #5b6cff',
      'box-shadow:0 12px 28px rgba(15,19,32,0.25)',
      'font:600 12px Inter, system-ui, sans-serif',
      'color:#0f1320',
      'transform:rotate(-2deg)',
    ].join(';');

    // Stack container: width grows with extra thumbs (each one peeks ~10px).
    const stackEl = document.createElement('div');
    const visibleStack = stack.slice(0, 3);
    const stackWidth = 40 + (visibleStack.length - 1) * 10;
    stackEl.style.cssText = [
      'position:relative',
      `width:${stackWidth}px`,
      'height:40px',
      'flex-shrink:0',
    ].join(';');

    visibleStack.forEach((m, i) => {
      const t = document.createElement('div');
      t.style.cssText = [
        'position:absolute',
        'top:0',
        `left:${i * 10}px`,
        'width:40px',
        'height:40px',
        'border-radius:6px',
        'background:#eef0ff center/cover no-repeat',
        `background-image:url("${escapeUrlForCss(m.url)}")`,
        'border:2px solid #fff',
        'box-shadow:0 1px 4px rgba(15,19,32,0.18)',
        // Anchor renders on top so it's the dominant thumb.
        `z-index:${visibleStack.length - i}`,
      ].join(';');
      stackEl.appendChild(t);
    });

    // Count badge on the corner of the stack when bulk-dragging.
    if (count > 1) {
      const badge = document.createElement('span');
      badge.textContent = `+${count}`;
      badge.style.cssText = [
        'position:absolute',
        'top:-6px',
        `left:${stackWidth - 14}px`,
        'min-width:20px',
        'height:18px',
        'padding:0 6px',
        'border-radius:9px',
        'background:#5b6cff',
        'color:#fff',
        'font-size:10px',
        'font-weight:700',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'box-shadow:0 1px 3px rgba(15,19,32,0.25)',
      ].join(';');
      stackEl.appendChild(badge);
    }

    const label = document.createElement('div');
    label.style.cssText = 'display:flex;flex-direction:column;line-height:1.2;';

    const top = document.createElement('span');
    top.textContent = count > 1 ? `${count} items` : (stack[0]?.filename || 'Image');
    top.style.maxWidth = '160px';
    top.style.overflow = 'hidden';
    top.style.textOverflow = 'ellipsis';
    top.style.whiteSpace = 'nowrap';

    const sub = document.createElement('span');
    sub.textContent = count > 1 ? 'Drop on a folder to move' : 'Move';
    sub.style.cssText = 'font-weight:500;font-size:10px;color:#65728a;';

    label.appendChild(top);
    label.appendChild(sub);
    wrap.appendChild(stackEl);
    wrap.appendChild(label);
    document.body.appendChild(wrap);

    // Anchor offset roughly under the cursor.
    e.dataTransfer.setDragImage(wrap, 24, 20);
    setTimeout(() => {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    }, 0);
  }

  function commit() {
    onSelect?.(pickedUrl);
    onClose();
  }

  const bulkCount = bulkIds.size;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Media library"
      wide
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={commit} className="btn-primary" disabled={!pickedUrl}>
            Use selected
          </button>
        </>
      }
    >
      <div className="grid grid-cols-[12rem,1fr] gap-4">
        <FolderSidebar
          tree={folderTree}
          active={folder}
          expanded={expanded}
          dragOverFolder={dragOverFolder}
          onPick={setFolder}
          onToggle={toggleExpanded}
          onNew={newFolder}
          onDragOver={onFolderDragOver}
          onDragLeave={onFolderDragLeave}
          onDrop={onFolderDrop}
        />
        <div className="min-w-0">
          <div className="mb-3 flex items-center gap-2">
            <input
              className="input flex-1 text-sm"
              placeholder="Search by name, type, or alt…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <label className="btn-secondary cursor-pointer text-sm">
              {uploading
                ? `${progress?.done ?? 0}/${progress?.total ?? '?'}`
                : '↑ Upload'}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={pickFiles}
                disabled={uploading}
              />
            </label>
          </div>

          {bulkCount > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs">
              <span className="font-semibold text-brand-700">
                {bulkCount} selected
              </span>
              <span className="text-ink-400">·</span>
              <button onClick={() => bulkMoveToFolder()} className="text-ink-700 hover:underline">
                Move to folder…
              </button>
              <span className="text-ink-400">·</span>
              <button onClick={bulkDelete} className="text-red-600 hover:underline">
                Delete
              </button>
              <button
                onClick={() => setBulkIds(new Set())}
                className="ml-auto text-ink-500 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          {progress && (
            <div className="mb-3 truncate rounded-md bg-ink-100 px-3 py-1 text-xs text-ink-600 dark:bg-ink-800 dark:text-ink-300">
              Uploading {progress.done}/{progress.total} · {progress.label || ''}
            </div>
          )}

          {failedUploads.length > 0 && !uploading && (
            <div className="mb-3 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <div className="flex-1">
                <div className="font-semibold">
                  {failedUploads.length} upload
                  {failedUploads.length === 1 ? '' : 's'} failed
                </div>
                <ul className="mt-1 max-h-24 overflow-y-auto pl-4 text-[11px] opacity-80">
                  {failedUploads.map((f, i) => (
                    <li key={i} className="truncate">
                      {f.file?.name || 'unknown'} — {f.error}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={retryFailed}
                  className="btn-secondary h-7 px-2 text-xs"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => setFailedUploads([])}
                  className="text-[11px] text-red-600 hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="grid place-items-center py-12 text-sm text-ink-500">
              Loading library…
            </div>
          ) : visible.length === 0 ? (
            <div className="grid place-items-center py-12 text-sm text-ink-500">
              {query
                ? `No matches for "${query}".`
                : 'No media here yet. Upload images via the button above, or drag files in.'}
            </div>
          ) : (
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="scroll-pane relative max-h-[55vh] overflow-y-auto pr-1"
            >
              {/* Spacer that gives the scrollbar a real total height — the
                  visible slice is positioned absolutely inside it. */}
              <div style={{ height: grid.totalHeight, position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: grid.offsetTop,
                    left: 0,
                    right: 0,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))`,
                    gap: grid.gap,
                  }}
                >
                  {visible.slice(grid.startIndex, grid.endIndex).map((m) => (
                    <Tile
                      key={m.id}
                      media={m}
                      folders={folders}
                      picked={pickedUrl === m.url}
                      bulked={bulkIds.has(m.id)}
                      onPick={() => setPickedUrl(m.url)}
                      onToggleBulk={(withShift) => toggleBulk(m, withShift)}
                      onDelete={() => deleteOne(m)}
                      onPatch={(payload) => patchMedia(m, payload)}
                      onDragStart={(e) => onTileDragStart(e, m)}
                    />
                  ))}
                </div>
              </div>
              {hasMore && (
                <div className="grid place-items-center py-3 text-xs text-ink-500">
                  {loadingMore ? 'Loading more…' : ''}
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div className="py-3 text-center text-[11px] text-ink-400">
                  End of library
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function sanitizePath(value) {
  return String(value || '').trim().replace(/^\/+|\/+$/g, '').replace(/\/+/g, '/');
}

// Escape a URL for safe interpolation into a CSS `url("…")` string. Stops a
// stray `"` in a media URL from breaking out of the literal.
function escapeUrlForCss(url) {
  return String(url).replace(/"/g, '\\"');
}

function buildFolderTree(paths) {
  const root = {};
  for (const p of paths) {
    const parts = p.split('/').filter(Boolean);
    let cur = root;
    for (const seg of parts) {
      if (!cur[seg]) cur[seg] = {};
      cur = cur[seg];
    }
  }
  return root;
}

// ── folder sidebar ───────────────────────────────────────────────────────

function FolderSidebar({
  tree,
  active,
  expanded,
  dragOverFolder,
  onPick,
  onToggle,
  onNew,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  return (
    <aside className="space-y-1 border-r border-ink-200 pr-3 text-sm dark:border-ink-800">
      <FolderItem
        label="All media"
        active={active === ALL_FOLDERS}
        onClick={() => onPick(ALL_FOLDERS)}
      />
      <FolderItem
        label="No folder"
        active={active === ROOT_FOLDER}
        dragHover={dragOverFolder === ROOT_FOLDER}
        onClick={() => onPick(ROOT_FOLDER)}
        onDragOver={(e) => onDragOver(e, ROOT_FOLDER)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, ROOT_FOLDER)}
        subtle
      />
      <div className="my-2 h-px bg-ink-100 dark:bg-ink-800" />
      <FolderTree
        node={tree}
        path=""
        active={active}
        expanded={expanded}
        dragOverFolder={dragOverFolder}
        onPick={onPick}
        onToggle={onToggle}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      />
      <button
        type="button"
        onClick={onNew}
        className="mt-2 w-full rounded-md border border-dashed border-ink-200 px-2 py-1 text-left text-xs text-ink-500 hover:bg-ink-50 dark:border-ink-800 dark:hover:bg-ink-800"
      >
        + New folder
      </button>
    </aside>
  );
}

function FolderTree({
  node,
  path,
  depth = 0,
  active,
  expanded,
  dragOverFolder,
  onPick,
  onToggle,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const entries = Object.keys(node).sort();
  return (
    <>
      {entries.map((name) => {
        const childPath = path ? `${path}/${name}` : name;
        const childNode = node[name];
        const hasChildren = Object.keys(childNode).length > 0;
        const isExpanded = expanded.has(childPath);
        const isActive = active === childPath;
        const isDragHover = dragOverFolder === childPath;
        return (
          <div key={childPath}>
            <div
              onDragOver={(e) => onDragOver(e, childPath)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, childPath)}
              className={clsx(
                'flex items-center gap-1 rounded-md transition',
                isActive
                  ? 'bg-brand-50 text-brand-700 dark:bg-ink-800 dark:text-ink-50'
                  : 'text-ink-700 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
                isDragHover && 'ring-2 ring-brand-500',
              )}
              style={{ paddingLeft: 4 + depth * 12 }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => onToggle(childPath)}
                  className="grid h-5 w-4 place-items-center text-ink-400"
                >
                  {isExpanded ? '▾' : '▸'}
                </button>
              ) : (
                <span className="inline-block w-4" />
              )}
              <button
                type="button"
                onClick={() => onPick(childPath)}
                className="flex-1 truncate px-1 py-1 text-left"
                title={childPath}
              >
                {name}
              </button>
            </div>
            {hasChildren && isExpanded && (
              <FolderTree
                node={childNode}
                path={childPath}
                depth={depth + 1}
                active={active}
                expanded={expanded}
                dragOverFolder={dragOverFolder}
                onPick={onPick}
                onToggle={onToggle}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function FolderItem({ label, active, subtle, dragHover, onClick, onDragOver, onDragLeave, onDrop }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={clsx(
        'block w-full truncate rounded-md px-2 py-1 text-left transition',
        active
          ? 'bg-brand-50 text-brand-700 dark:bg-ink-800 dark:text-ink-50'
          : subtle
            ? 'text-ink-400 hover:bg-ink-100 dark:hover:bg-ink-800'
            : 'text-ink-700 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800',
        dragHover && 'ring-2 ring-brand-500',
      )}
    >
      {label}
    </button>
  );
}

// ── tile ─────────────────────────────────────────────────────────────────

function Tile({
  media,
  folders,
  picked,
  bulked,
  onPick,
  onToggleBulk,
  onDelete,
  onPatch,
  onDragStart,
}) {
  const [alt, setAlt] = useState(media.alt || '');
  const [showDetails, setShowDetails] = useState(false);

  function commitAlt() {
    if ((alt || '') === (media.alt || '')) return;
    onPatch({ alt });
  }

  function moveToFolder(e) {
    const value = e.target.value;
    onPatch({ folder: value === '' ? null : value });
  }

  function onCheckboxClick(e) {
    e.stopPropagation();
    onToggleBulk(e.shiftKey);
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={clsx(
        'group relative flex flex-col overflow-hidden rounded-lg border-2 bg-ink-50 transition dark:bg-ink-950',
        picked
          ? 'border-brand-500 shadow-md'
          : bulked
            ? 'border-brand-300'
            : 'border-transparent hover:border-ink-200',
      )}
    >
      <button
        type="button"
        onClick={onCheckboxClick}
        className={clsx(
          'absolute left-1 top-1 z-10 grid h-5 w-5 place-items-center rounded border-2 text-[10px] transition',
          bulked
            ? 'border-brand-500 bg-brand-500 text-white'
            : 'border-white/80 bg-white/70 text-transparent group-hover:border-brand-400 group-hover:text-brand-500',
        )}
        title="Select for bulk action (shift-click for range)"
      >
        ✓
      </button>
      <button
        type="button"
        onClick={onPick}
        onDoubleClick={() => setShowDetails((v) => !v)}
        className="grid h-28 place-items-center overflow-hidden"
        title="Click to use · double-click for details · drag to a folder to move"
      >
        <img
          src={media.url}
          alt={media.alt || media.filename}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </button>
      <div className="flex items-center justify-between gap-1 px-2 py-1 text-[10px] text-ink-500">
        <span className="truncate" title={media.filename}>{media.filename}</span>
        <span className="shrink-0">{formatBytes(media.size_bytes)}</span>
      </div>
      {showDetails && (
        <div className="space-y-1 border-t border-ink-200 p-2 text-[11px] dark:border-ink-800">
          <input
            className="input h-7 text-[11px]"
            placeholder="Alt text"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            onBlur={commitAlt}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
          <select
            className="input h-7 text-[11px]"
            value={media.folder || ''}
            onChange={moveToFolder}
          >
            <option value="">No folder</option>
            {folders.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1 top-1 hidden h-6 w-6 place-items-center rounded-full bg-white/90 text-xs text-red-500 shadow-sm group-hover:grid"
        title="Delete from library"
      >
        ×
      </button>
    </div>
  );
}

// ── virtual grid hook ────────────────────────────────────────────────────
// Minimal grid virtualizer: tracks the scroll container's size + scrollTop,
// derives column count from container width, returns the slice [startIndex,
// endIndex) of items to render along with `offsetTop` and `totalHeight` so
// the parent can position the slice absolutely inside a sized spacer.
//
// Trade-offs:
// - Fixed `rowHeight` — tiles vary slightly when the details panel opens,
//   which can make subsequent rows briefly misalign. Acceptable for v0.12.
// - Doesn't support `position: sticky` children (none here).
// - Uses ResizeObserver, present everywhere we run.
function useVirtualGrid(scrollRef, count, opts) {
  const { rowHeight, minColumnWidth, gap, overscan = 4 } = opts;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWidth(el.clientWidth);
      setHeight(el.clientHeight);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    setHeight(el.clientHeight);
    return () => ro.disconnect();
  }, [scrollRef]);

  function handleScroll(e) {
    setScrollTop(e.currentTarget.scrollTop);
  }

  const columns = Math.max(1, Math.floor((width + gap) / (minColumnWidth + gap)));
  const totalRows = Math.ceil(count / columns);
  const totalHeight = Math.max(0, totalRows * rowHeight - gap);

  const viewportHeight = height || 480;
  const startRow = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endRow = Math.min(
    totalRows,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
  );

  const startIndex = startRow * columns;
  const endIndex = Math.min(count, endRow * columns);
  const offsetTop = startRow * rowHeight;

  return {
    columns,
    totalHeight,
    startIndex,
    endIndex,
    offsetTop,
    rowHeight,
    gap,
    handleScroll,
  };
}

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
